/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import { useNavigate, Link } from 'react-router-dom';
import Pagination from "react-js-pagination";
import Navbar from "../Navbar";
import Header from "../Header";
import baseUrl from "../BaseUrl";
import UserSegmentToggle from "../common/UserSegmentToggle";
import { useUserSegment, watchFilterValue } from "../../context/UserSegmentContext";
import ExportButton from "../active-users/ExportButton";
import DateRangeBar, { fmt, presetRange } from "../active-users/DateRangeBar";
import EmptyState from "../active-users/states/EmptyState";
import ErrorState from "../active-users/states/ErrorState";
import TableSkeleton from "../active-users/states/TableSkeleton";
import PauseAlertImpactSummaryCards from "./PauseAlertImpactSummaryCards";
import "../active-users/active-users.css";
import "./PauseAlertImpact.css";
import { formatUTCDate } from '../../utils/utils';

const LIMIT = 20;
const STATE_KEY = "pauseAlertImpactListState";

const EXPORT_COLUMNS = [
  { key: 'user_id', label: 'User ID' },
  { key: 'email', label: 'Email' },
  { key: 'register_time', label: 'Registered' },
  { key: 'last_active', label: 'Last Active (UTC)' },
  { key: 'breath_sessions', label: 'Breath Sessions' },
  { key: 'pause_start', label: 'Pause Start' },
  { key: 'pause_end', label: 'Pause End' },
  { key: 'alerts_before', label: 'Alerts Before' },
  { key: 'alerts_during', label: 'Alerts During' },
  { key: 'alerts_per_day_before', label: 'Alerts/Day Before' },
  { key: 'alerts_per_day_during', label: 'Alerts/Day During' },
  { key: 'rate_delta', label: 'Rate Delta' },
  { key: 'alert_trend', label: 'Trend' },
];

const TREND_LABEL = {
  decreased: 'Decreased',
  increased: 'Increased',
  unchanged: 'No change',
  no_baseline: 'No baseline',
};

const PauseAlertImpactList = () => {
  const navigate = useNavigate();
  const { segment } = useUserSegment();
  // NOTE (research R-004): this rides on `user_segment`, NEVER `watch_filter`.
  // On breath-backed pages `watch_filter` already means breath_data.is_from_watch,
  // a record-level device flag — a different concept from this user-level segment.
  const userSegment = watchFilterValue(segment) || 'all';

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [rows, setRows] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [subscribedUserCount, setSubscribedUserCount] = useState(0);
  const [page, setPage] = useState(1);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFromDate, setDisplayFromDate] = useState(null);
  const [displayToDate, setDisplayToDate] = useState(null);
  const [preset, setPreset] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('');
  const [trendFilter, setTrendFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  const [summary, setSummary] = useState({ total_users: 0, buckets: {}, subscribedUserCount: 0 });

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => {
    let restored = false;
    try {
      const saved = JSON.parse(sessionStorage.getItem(STATE_KEY) || 'null');
      if (saved) {
        restored = true;
        setFromDate(saved.fromDate || ''); setToDate(saved.toDate || '');
        setDisplayFromDate(saved.fromDate ? new Date(saved.fromDate) : null);
        setDisplayToDate(saved.toDate ? new Date(saved.toDate) : null);
        setPreset(saved.preset || null);
        setSearchValue(saved.searchValue || ''); setSubscriptionFilter(saved.subscriptionFilter || '');
        setTrendFilter(saved.trendFilter || '');
        setSortField(saved.sortField || ''); setSortOrder(saved.sortOrder || ''); setPage(saved.page || 1);
      }
    } catch { /* ignore */ }
    // Unlike the other activity pages this one defaults to NO date range: the pause
    // window is per-user and lifetime-based by default, and a preset range would
    // silently reshape every window on first load (FR-004).
    if (!restored) { setPreset(null); }
    refreshToken();
  }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  useEffect(() => {
    if (token) loadAll(page, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter, trendFilter);
  }, [token]);

  const didMountSegment = useRef(false);
  useEffect(() => {
    if (!didMountSegment.current) { didMountSegment.current = true; return; }
    if (!token) return;
    setPage(1);
    loadAll(1, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter, trendFilter);
  }, [segment]);

  useEffect(() => {
    sessionStorage.setItem(STATE_KEY, JSON.stringify({
      fromDate, toDate, preset, searchValue, subscriptionFilter, trendFilter, sortField, sortOrder, page,
    }));
  }, [fromDate, toDate, preset, searchValue, subscriptionFilter, trendFilter, sortField, sortOrder, page]);

  const qs = (p, f, t, s, sf, so, sub, tr) =>
    `page=${p}&limit=${LIMIT}&from_date=${f}&to_date=${t}&search=${s}&sortField=${sf}&sortOrder=${so}` +
    `&subscription_status=${sub}&alert_trend=${tr}&user_segment=${userSegment}`;
  const rangeQs = (f, t, s, sub, tr) =>
    `from_date=${f}&to_date=${t}&search=${s}&subscription_status=${sub}&alert_trend=${tr}&user_segment=${userSegment}`;
  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const applyList = (d) => {
    setRows(d.data || []);
    setTotalItem(d.totalItem || 0);
    setSubscribedUserCount(d.subscribedUserCount || 0);
  };
  const handleErr = (err) => { setError(err?.response?.status === 401 ? 'auth' : 'load'); };

  const getList = async (p, f, t, s, sf, so, sub, tr) => {
    setLoading(true); setError('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/pause-alert-impact?${qs(p, f, t, s, sf, so, sub, tr)}`, auth());
      applyList(res.data);
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const loadAll = async (p, f, t, s, sf, so, sub, tr) => {
    setLoading(true); setError('');
    try {
      const [list, sum] = await Promise.all([
        axiosJWT.get(`${baseUrl}/admin/api/pause-alert-impact?${qs(p, f, t, s, sf, so, sub, tr)}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/pause-alert-impact-summary?${rangeQs(f, t, s, sub, tr)}`, auth()),
      ]);
      applyList(list.data);
      setSummary(sum.data.data || { total_users: 0, buckets: {}, subscribedUserCount: 0 });
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
    getList(pageNumber, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter, trendFilter);
  };
  const handleFromChange = (date) => { setPreset('custom'); setDisplayFromDate(date); setFromDate(date ? fmt(date) : ''); };
  const handleToChange = (date) => { setPreset('custom'); setDisplayToDate(date); setToDate(date ? fmt(date) : ''); };
  const handlePreset = (key, fromStr, toStr) => {
    const { fromStr: fs, toStr: ts } = presetRange(key, new Date());
    setPreset(key); setFromDate(fromStr || fs); setToDate(toStr || ts);
    setDisplayFromDate(new Date(fromStr || fs)); setDisplayToDate(new Date(toStr || ts));
    setPage(1);
    loadAll(1, fromStr || fs, toStr || ts, searchValue, sortField, sortOrder, subscriptionFilter, trendFilter);
  };
  const handleSearch = () => { setPage(1); loadAll(1, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter, trendFilter); };
  const handleSubscription = (e) => {
    const v = e.target.value;
    setSubscriptionFilter(v); setPage(1);
    loadAll(1, fromDate, toDate, searchValue, sortField, sortOrder, v, trendFilter);
  };
  const handleTrend = (e) => {
    const v = e.target.value;
    setTrendFilter(v); setPage(1);
    loadAll(1, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter, v);
  };
  const clearFilter = () => {
    setFromDate(''); setToDate(''); setDisplayFromDate(null); setDisplayToDate(null);
    setSearchValue(''); setSubscriptionFilter(''); setTrendFilter(''); setPreset(null); setPage(1);
    loadAll(1, '', '', '', sortField, sortOrder, '', '');
  };
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setSortField(field); setSortOrder(order); setPage(1);
    loadAll(1, fromDate, toDate, searchValue, field, order, subscriptionFilter, trendFilter);
  };

  const fetchExport = async () => {
    const res = await axiosJWT.get(
      `${baseUrl}/admin/api/pause-alert-impact-export?${rangeQs(fromDate, toDate, searchValue, subscriptionFilter, trendFilter)}` +
      `&sortField=${sortField}&sortOrder=${sortOrder}`,
      auth());
    return res.data.data || [];
  };

  const SortTh = ({ field, label }) => (
    <th className="au-th-sort" onClick={() => handleSort(field)}>
      {label} {sortField === field ? (sortOrder === 'ASC' ? '🔼' : '🔽') : ''}
    </th>
  );

  // Drill into one user's day-by-day detail (feature 042). Listing filter state is
  // already persisted to sessionStorage, so returning restores the view (FR-004).
  const drill = (id) => { navigate(`/pause-alert-impact-details/${btoa(id)}`); };

  const hasRange = !!(fromDate || toDate || searchValue || subscriptionFilter || trendFilter);
  const dmy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Activity › <b>Pause → alert impact</b></span>
            <span className="au-route">/pause-alert-impact</span>
            <span className="au-spacer" />
            <UserSegmentToggle />
            <span className="au-back"><Link to="/dashboard">← Back</Link></span>
          </div>
          <h1 className="au-title">Pause → Alert Impact</h1>

          <p className="au-range-note pai-scope-note" data-testid="pai-scope-note">
            On this page a <b>pause</b> means a <b>breath session only</b> — meditation sessions are
            excluded, so these counts will not match the Pauses page. An <b>alert</b> is a heart-rate
            session. Each user is compared against their own 30 days before they started pausing.
          </p>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load the pause / alert impact data. Please try again."
              onRetry={() => loadAll(page, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter, trendFilter)} />
          ) : (
            <>
              <PauseAlertImpactSummaryCards
                totalUsers={summary.total_users}
                buckets={summary.buckets}
                subscribedUserCount={summary.subscribedUserCount || subscribedUserCount}
              />

              <DateRangeBar
                leading={(
                  <>
                    <input className="au-input" type="text" placeholder="Search by name or email"
                      value={searchValue} onChange={(e) => setSearchValue(e.target.value)} aria-label="Search users" />
                    <select className="au-select" value={subscriptionFilter} onChange={handleSubscription} aria-label="Subscription filter">
                      <option value="">All Subscriptions</option>
                      <option value="1">Active</option>
                      <option value="2">Cancel</option>
                      <option value="4">Expired</option>
                    </select>
                    <select className="au-select" value={trendFilter} onChange={handleTrend} aria-label="Alert trend filter">
                      <option value="">All Trends</option>
                      <option value="decreased">Decreased</option>
                      <option value="increased">Increased</option>
                      <option value="unchanged">No change</option>
                      <option value="no_baseline">No baseline</option>
                    </select>
                  </>
                )}
                preset={preset}
                displayFrom={displayFromDate}
                displayTo={displayToDate}
                hasRange={hasRange}
                onPreset={handlePreset}
                onFromChange={handleFromChange}
                onToChange={handleToChange}
                onSearch={handleSearch}
                onClear={clearFilter}
              />
              <p className="au-range-note" data-testid="pai-range-note">
                The date range decides which breath sessions define each user’s pause window. The
                30-day baseline is always measured immediately <b>before</b> that window, so it sits
                outside the range by design.{' '}
                {(fromDate || toDate)
                  ? <>Showing <b>{dmy(fromDate) || '…'}</b> – <b>{dmy(toDate) || '…'}</b>.</>
                  : 'Showing each user’s full lifetime pause window (no date filter).'}
              </p>

              {loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : rows.length === 0 ? (
                <EmptyState title="No qualifying users in this range" hasFilters={hasRange} onClear={clearFilter}
                  message={hasRange
                    ? 'No users match the current filters. Users need breath sessions spanning at least 7 days to appear.'
                    : 'No users yet have breath sessions spanning at least 7 days.'} />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">Per-user alert impact</h4>
                    <span className="au-spacer" />
                    <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename="pause-alert-impact.csv" />
                  </div>
                  <div className="au-table-wrap">
                    <table className="au-table pai-table" data-testid="pai-table">
                      <thead>
                        <tr>
                          <th>No</th><th>Email</th>
                          <SortTh field="register_time" label="Registered" />
                          <SortTh field="last_active" label="Last Active (UTC)" />
                          <SortTh field="breath_sessions" label="Breath Sessions" />
                          <SortTh field="pause_start" label="Pause Start" />
                          <SortTh field="pause_end" label="Pause End" />
                          <SortTh field="alerts_before" label="Alerts Before" />
                          <SortTh field="alerts_during" label="Alerts During" />
                          <SortTh field="alerts_per_day_before" label="Alerts/Day Before" />
                          <SortTh field="alerts_per_day_during" label="Alerts/Day During" />
                          <SortTh field="rate_delta" label="Rate Delta" />
                          <th>Trend</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, index) => (
                          <tr key={`${row.user_id}-${index}`}>
                            <td data-label="No">{(page - 1) * LIMIT + index + 1}</td>
                            <td data-label="Email" className="au-ellipsis">{row.email}</td>
                            <td data-label="Registered">{formatUTCDate(row.register_time) || '-'}</td>
                            {/* Null last_active means the user has never opened the app —
                                shown as "Never", not as an em dash, so it reads as a fact
                                rather than missing data. */}
                            <td data-label="Last Active (UTC)" className={row.last_active ? '' : 'pai-never'}>
                              {formatUTCDate(row.last_active) || 'Never'}
                            </td>
                            <td data-label="Breath Sessions" className="tnum">
                              <Link className="au-link" to={`/pause-alert-impact-details/${btoa(row.user_id)}`}
                                onClick={(e) => { e.preventDefault(); drill(row.user_id); }}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); drill(row.user_id); } }}>
                                {row.breath_sessions}
                              </Link>
                            </td>
                            <td data-label="Pause Start">{formatUTCDate(row.pause_start) || '-'}</td>
                            <td data-label="Pause End">{formatUTCDate(row.pause_end) || '-'}</td>
                            <td data-label="Alerts Before" className="tnum">{row.alerts_before}</td>
                            <td data-label="Alerts During" className="tnum">{row.alerts_during}</td>
                            <td data-label="Alerts/Day Before" className="tnum">{row.alerts_per_day_before}</td>
                            <td data-label="Alerts/Day During" className="tnum">{row.alerts_per_day_during}</td>
                            <td data-label="Rate Delta" className={`tnum ${Number(row.rate_delta) < 0 ? 'pai-delta-down' : Number(row.rate_delta) > 0 ? 'pai-delta-up' : ''}`}>
                              {row.rate_delta}
                            </td>
                            <td data-label="Trend">
                              <span className={`pai-badge pai-badge--${row.alert_trend}`} data-testid={`pai-trend-${row.user_id}`}>
                                {TREND_LABEL[row.alert_trend] || row.alert_trend}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalItem > LIMIT && (
                    <Pagination activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={totalItem}
                      pageRangeDisplayed={5} onChange={handlePageChange} />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PauseAlertImpactList;
